import React, {
  useEffect,
  useState,
} from "react";

import {
  Moon,
  Sun,
} from "lucide-react";

import i18next from "i18next";

import "./ReisorProThemeToggle.css";

const STORAGE_KEY =
  "uiTheme";

const LEGACY_STORAGE_KEY =
  "reisor_pro_theme";

const ATTRIBUTE =
  "data-theme";

const THEME_EVENT =
  "reisor-theme-change";

const COPY = {
  ru: {
    light: "Светлая тема",
    dark: "Тёмная тема",
  },
  uz: {
    light: "Ёруғ мавзу",
    dark: "Қоронғи мавзу",
  },
  tr: {
    light: "Açık tema",
    dark: "Koyu tema",
  },
};

function normalizeTheme(
  value
) {
  return value === "dark"
    ? "dark"
    : "light";
}

function languageCode() {
  const raw = String(
    i18next.resolvedLanguage ||
      i18next.language ||
      "ru"
  )
    .toLowerCase()
    .slice(0, 2);

  return COPY[raw]
    ? raw
    : "ru";
}

function readTheme() {
  try {
    const canonical =
      localStorage.getItem(
        STORAGE_KEY
      );

    if (
      canonical === "light" ||
      canonical === "dark"
    ) {
      return canonical;
    }

    const legacy =
      localStorage.getItem(
        LEGACY_STORAGE_KEY
      );

    if (
      legacy === "light" ||
      legacy === "dark"
    ) {
      localStorage.setItem(
        STORAGE_KEY,
        legacy
      );

      localStorage.removeItem(
        LEGACY_STORAGE_KEY
      );

      return legacy;
    }
  } catch {}

  return "light";
}

function applyTheme(
  value,
  notify = false
) {
  const theme =
    normalizeTheme(
      value
    );

  try {
    const root =
      document.documentElement;

    root.setAttribute(
      ATTRIBUTE,
      theme
    );

    root.classList.toggle(
      "dark",
      theme === "dark"
    );

    root.setAttribute("data-reisor-pro-theme", theme);
    root.style.colorScheme =
      theme;

    localStorage.setItem(
      STORAGE_KEY,
      theme
    );

    localStorage.removeItem(
      LEGACY_STORAGE_KEY
    );

    if (notify) {
      window.dispatchEvent(
        new CustomEvent(
          THEME_EVENT,
          {
            detail: theme,
          }
        )
      );
    }
  } catch {}

  return theme;
}

applyTheme(
  readTheme()
);

export default function ReisorProThemeToggle({
  compact = false,
}) {
  const [
    theme,
    setTheme,
  ] = useState(
    () => readTheme()
  );

  const t =
    COPY[languageCode()] ||
    COPY.ru;

  const light =
    theme === "light";

  const nextLabel =
    light
      ? t.dark
      : t.light;

  useEffect(() => {
    applyTheme(
      theme
    );
  }, [theme]);

  useEffect(() => {
    const onThemeChange = (
      event
    ) => {
      const next =
        event?.detail;

      if (
        next !== "light" &&
        next !== "dark"
      ) {
        return;
      }

      setTheme(
        next
      );
    };

    const onStorage = (
      event
    ) => {
      if (
        event?.key !==
        STORAGE_KEY
      ) {
        return;
      }

      if (
        event?.newValue !==
          "light" &&
        event?.newValue !==
          "dark"
      ) {
        return;
      }

      setTheme(
        event.newValue
      );
    };

    window.addEventListener(
      THEME_EVENT,
      onThemeChange
    );

    window.addEventListener(
      "storage",
      onStorage
    );

    return () => {
      window.removeEventListener(
        THEME_EVENT,
        onThemeChange
      );

      window.removeEventListener(
        "storage",
        onStorage
      );
    };
  }, []);

  function toggleTheme() {
    const next =
      theme === "light"
        ? "dark"
        : "light";

    setTheme(
      next
    );

    applyTheme(
      next,
      true
    );
  }

  return (
    <button
      type="button"
      className={`rp-theme-toggle${
        compact
          ? " rp-theme-toggle--compact"
          : ""
      }`}
      onClick={toggleTheme}
      aria-label={nextLabel}
      title={nextLabel}
      data-reisor-theme-toggle="canonical-v2"
    >
      {light ? (
        <Moon
          size={18}
          aria-hidden="true"
        />
      ) : (
        <Sun
          size={18}
          aria-hidden="true"
        />
      )}

      <span>
        {nextLabel}
      </span>
    </button>
  );
}
